import { useLang } from "../i18n";
import { useSEO } from "../seo";
import { FadeIn, ParallaxImage } from "../components/Reveal";
import { CTAButton, PageHero, SectionLabel } from "../components/Bits";

const IMG = {
  hero: "https://images.pexels.com/photos/36576285/pexels-photo-36576285.jpeg?auto=compress&cs=tinysrgb&w=2000",
  timeline: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?q=80&w=1600&auto=format&fit=crop",
};

const copy = {
  it: {
    seoTitle: "Operazioni Immobiliari | Nicole Srl",
    seoDesc: "Acquisizione diretta, progetto, trasformazione e rivendita: il modello operativo di Nicole Srl per la valorizzazione immobiliare.",
    kicker: "Operazioni immobiliari",
    lines: ["Dal potenziale", "al valore."],
    sub: "Acquistiamo direttamente immobili con potenziale inespresso e li riportiamo sul mercato completamente riposizionati. Ogni operazione segue un percorso preciso, dall'individuazione alla rivendita.",
    timelineLabel: "Il modello",
    steps: [
      { n: "01", title: "Scouting / Acquisizione", text: "Individuiamo immobili con potenziale di valorizzazione, analizzando posizione, caratteristiche, criticità e condizioni di acquisto." },
      { n: "02", title: "Progetto", text: "Studiamo layout, target, design, materiali e posizionamento: la strategia dell'operazione nasce prima del cantiere." },
      { n: "03", title: "Trasformazione", text: "Coordiniamo la riqualificazione dell'immobile, seguendo direttamente lavori, fornitori e qualità esecutiva." },
      { n: "04", title: "Rivendita", text: "Riportiamo sul mercato un prodotto immobiliare completamente riposizionato, coerente con il target finale." },
    ],
    buyLabel: "Cosa acquistiamo",
    buyTitle: "Le opportunità che valutiamo.",
    buyNote: "Non sono criteri rigidi: ogni immobile viene valutato singolarmente, con rapidità e riservatezza.",
    buyItems: [
      "Appartamenti da ristrutturare",
      "Immobili da valorizzare",
      "Immobili di grandi dimensioni frazionabili",
      "Uffici e immobili non residenziali con potenziale di trasformazione",
      "Immobili con possibile cambio di destinazione d'uso",
      "Immobili provenienti da successioni",
      "Immobili con problematiche debitorie",
      "Immobili interessati da procedure esecutive",
      "Piccoli stabili",
      "Opportunità off-market",
      "Operazioni immobiliari da sviluppare",
    ],
    ctaTitle: "Possiedi un immobile con queste caratteristiche?",
    ctaText: "Inviaci le informazioni essenziali: ti rispondiamo con una prima valutazione riservata.",
  },
  en: {
    seoTitle: "Real Estate Operations | Nicole Srl",
    seoDesc: "Direct acquisition, design, transformation and resale: Nicole Srl's operating model for real estate value creation.",
    kicker: "Real estate operations",
    lines: ["From potential", "to value."],
    sub: "We directly acquire properties with unexpressed potential and bring them back to the market completely repositioned. Every operation follows a precise path, from sourcing to resale.",
    timelineLabel: "The model",
    steps: [
      { n: "01", title: "Sourcing / Acquisition", text: "We identify properties with value-creation potential, analysing location, features, criticalities and purchase conditions." },
      { n: "02", title: "Design", text: "We study layout, target, design, materials and positioning: the operation's strategy is defined before the works begin." },
      { n: "03", title: "Transformation", text: "We coordinate the property's redevelopment, directly supervising works, suppliers and execution quality." },
      { n: "04", title: "Resale", text: "We bring back to the market a completely repositioned real estate product, consistent with its final target." },
    ],
    buyLabel: "What we acquire",
    buyTitle: "The opportunities we assess.",
    buyNote: "These are not rigid criteria: every property is assessed individually, with speed and confidentiality.",
    buyItems: [
      "Apartments to renovate",
      "Properties with value potential",
      "Large properties suitable for subdivision",
      "Offices and non-residential properties with transformation potential",
      "Properties with possible change of use",
      "Properties from inheritances",
      "Properties with debt-related issues",
      "Properties under enforcement procedures",
      "Small buildings",
      "Off-market opportunities",
      "Real estate operations to develop",
    ],
    ctaTitle: "Do you own a property with these characteristics?",
    ctaText: "Send us the essential information: we will reply with a first confidential assessment.",
  },
};

export default function Operazioni() {
  const { lang } = useLang();
  const t = copy[lang];
  useSEO({ title: t.seoTitle, description: t.seoDesc, path: "/operazioni-immobiliari", lang });

  return (
    <div data-testid="operazioni-page">
      <PageHero kicker={t.kicker} lines={t.lines} sub={t.sub} image={IMG.hero} imageAlt={lang === "it" ? "Facciata storica milanese" : "Historic Milanese facade"} />

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
        <FadeIn>
          <SectionLabel>{t.timelineLabel}</SectionLabel>
        </FadeIn>
        <div className="mt-16 grid gap-14 md:mt-24 md:grid-cols-12">
          <div className="md:col-span-7">
            {t.steps.map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.06}>
                <div className="group grid gap-4 border-b border-ink/10 py-10 transition-colors duration-500 md:grid-cols-12 md:gap-8 md:py-12">
                  <div className="md:col-span-2">
                    <span className="font-serif text-5xl font-light text-ink/15 transition-colors duration-500 group-hover:text-bronze md:text-6xl">
                      {s.n}
                    </span>
                  </div>
                  <div className="md:col-span-4">
                    <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">{s.title}</h2>
                  </div>
                  <div className="md:col-span-6">
                    <p className="text-base leading-relaxed text-stone">{s.text}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn className="md:col-span-4 md:col-start-9" delay={0.15}>
            <div className="md:sticky md:top-32">
              <ParallaxImage src={IMG.timeline} alt={lang === "it" ? "Interno contemporaneo in ristrutturazione" : "Contemporary interior under renovation"} className="aspect-[3/4] w-full" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-dove/40">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
          <FadeIn>
            <SectionLabel>{t.buyLabel}</SectionLabel>
            <h2 className="mt-10 max-w-2xl font-serif text-4xl font-light leading-[1.08] tracking-tight md:mt-14 md:text-5xl">
              {t.buyTitle}
            </h2>
          </FadeIn>
          <div className="mt-14 grid gap-x-16 md:mt-20 md:grid-cols-2">
            {t.buyItems.map((item, i) => (
              <FadeIn key={item} delay={(i % 2) * 0.05}>
                <div className="flex items-baseline gap-5 border-b border-ink/10 py-5">
                  <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-bronze" />
                  <span className="text-base leading-snug">{item}</span>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.15}>
            <p className="mt-10 max-w-md text-sm leading-relaxed text-stone">{t.buyNote}</p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 text-center md:px-10 md:py-36">
        <FadeIn>
          <h2 className="mx-auto max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-5xl">
            {t.ctaTitle}
          </h2>
          <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-stone">{t.ctaText}</p>
          <div className="mt-10 flex justify-center">
            <CTAButton to="/contatti" variant="dark" testId="operazioni-cta">
              {lang === "it" ? "Proponici un immobile" : "Propose a property"}
            </CTAButton>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
